<!-- eslint-disable -->
<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="$router.push({ name: 'purchase-orders' })">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Détails de la commande</h1>
          <p class="text-xs text-gray-400">Consultez les informations complètes</p>
        </div>
      </div>
      <div v-if="po && po.id" class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="printPurchaseOrder">
          <i class="fas fa-print text-[11px]"></i>
          Imprimer
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          @click="showCancelModal = true" :disabled="po.status === 'cancelled'">
          <i class="fas fa-times text-[11px]"></i>
          Annuler
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement des détails...</p>
    </div>

    <!-- Commande introuvable -->
    <div v-else-if="!po || !po.id" class="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
      <div class="text-gray-300 mb-4">
        <i class="fas fa-exclamation-circle text-4xl"></i>
      </div>
      <h2 class="text-base font-semibold text-gray-700 mb-1">Commande introuvable</h2>
      <p class="text-sm text-gray-400 mb-6">Cette commande n'existe pas ou a été supprimée</p>
      <button
        class="px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
        @click="$router.push({ name: 'purchase-orders' })">
        <i class="fas fa-arrow-left mr-1.5 text-xs"></i>
        Retour à la liste
      </button>
    </div>

    <!-- Contenu principal -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte info commande -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-start justify-between mb-5">
            <div>
              <h2 class="text-base font-bold text-gray-900 font-mono">{{ po.reference }}</h2>
              <p class="text-xs text-gray-400 mt-0.5">
                <i class="fas fa-calendar mr-1 text-[10px]"></i>
                {{ formatDate(po.order_date) }}
              </p>
            </div>
            <div class="flex flex-col items-end gap-1.5">
              <span :class="[
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
                po.status === 'active' ? 'bg-green-50 text-green-700' :
                po.status === 'cancelled' ? 'bg-red-50 text-red-600' :
                'bg-gray-100 text-gray-600'
              ]">
                <span :class="[
                  'w-1.5 h-1.5 rounded-full',
                  po.status === 'active' ? 'bg-green-500' :
                  po.status === 'cancelled' ? 'bg-red-500' :
                  'bg-gray-400'
                ]"></span>
                {{ getStatusLabel(po.status) }}
              </span>
              <span v-if="po.has_invoice !== undefined"
                :class="[
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold',
                  po.has_invoice ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-600'
                ]">
                <i class="fas fa-file text-[9px]"></i>
                {{ po.has_invoice ? 'Facturée' : 'Non facturée' }}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Fournisseur</p>
              <p class="text-sm font-medium text-gray-900">{{ po.supplier?.name || '-' }}</p>
              <div v-if="po.supplier?.phone || po.supplier?.email" class="mt-1 space-y-0.5">
                <p v-if="po.supplier?.phone" class="text-xs text-gray-400">
                  <i class="fas fa-phone mr-1 text-[10px]"></i>{{ po.supplier.phone }}
                </p>
                <p v-if="po.supplier?.email" class="text-xs text-gray-400">
                  <i class="fas fa-envelope mr-1 text-[10px]"></i>{{ po.supplier.email }}
                </p>
              </div>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Créée par</p>
              <p class="text-sm font-medium text-gray-900">{{ po.user?.name || '-' }}</p>
              <p class="text-xs text-gray-400 mt-0.5">{{ formatDateTime(po.created_at) }}</p>
            </div>
            <div v-if="po.notes" class="col-span-2 md:col-span-1">
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Remarques</p>
              <div class="bg-gray-50 rounded-lg p-3 mt-1">
                <p class="text-sm text-gray-600 whitespace-pre-line">{{ po.notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tableau articles -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 py-4 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">
              Articles de la commande
              <span class="text-gray-400 font-normal">({{ po.items?.length || 0 }})</span>
            </h3>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full min-w-[700px]">
              <thead class="bg-gray-50/80">
                <tr>
                  <th class="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Produit</th>
                  <th class="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-16">Qté</th>
                  <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Prix achat</th>
                  <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Prix public</th>
                  <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-24">Remise</th>
                  <th class="px-5 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Total</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-50">
                <tr v-for="item in po.items || []" :key="item.id" class="hover:bg-gray-50/60 transition-colors">
                  <td class="px-5 py-3">
                    <p class="text-sm font-medium text-gray-900">{{ item.product?.name || ('#' + item.product_id) }}</p>
                    <p v-if="item.product?.reference" class="text-xs text-gray-400 mt-0.5">
                      Réf: {{ item.product.reference }}
                    </p>
                  </td>
                  <td class="px-3 py-3 text-center">
                    <span class="text-sm font-bold text-gray-900">{{ item.quantity }}</span>
                  </td>
                  <td class="px-3 py-3 text-right">
                    <span class="text-sm font-medium text-gray-900">{{ formatCurrency(item.purchase_price) }}</span>
                  </td>
                  <td class="px-3 py-3 text-right">
                    <span class="text-sm font-medium text-gray-900">{{ formatCurrency(item.sale_price) }}</span>
                  </td>
                  <td class="px-3 py-3 text-right">
                    <span v-if="item.discount > 0" class="text-sm font-medium text-red-500">-{{ formatCurrency(item.discount) }}</span>
                    <span v-else class="text-sm text-gray-300">-</span>
                  </td>
                  <td class="px-5 py-3 text-right">
                    <span class="text-sm font-bold text-primary">{{ formatCurrency(item.total_amount) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- État vide -->
          <div v-if="!po.items || po.items.length === 0" class="p-8 text-center text-sm text-gray-400">
            Aucun article
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Résumé -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden lg:sticky lg:top-5">
          <div class="px-5 py-3 bg-primary/5 border-b border-primary/10">
            <h3 class="text-sm font-semibold text-primary text-center">Résumé</h3>
          </div>
          <div class="p-5 space-y-2.5">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">
                <i class="fas fa-tag mr-1 text-[10px]"></i>
                Articles
              </span>
              <span class="text-sm font-semibold text-gray-900">{{ po.items?.length || 0 }}</span>
            </div>

            <div class="h-px bg-gray-100"></div>

            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">
                <i class="fas fa-shopping-cart mr-1 text-[10px]"></i>
                Total achat
              </span>
              <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(totalPurchase) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">
                <i class="fas fa-dollar-sign mr-1 text-[10px]"></i>
                Total public
              </span>
              <span class="text-sm font-medium text-gray-900">{{ formatCurrency(totalPublic) }}</span>
            </div>

            <div class="h-px bg-gray-100"></div>

            <div class="flex justify-between items-center pt-1">
              <span class="text-xs font-medium text-gray-500">
                <i class="fas fa-chart-line mr-1 text-[10px]"></i>
                Marge estimée
              </span>
              <span class="text-lg font-bold text-green-600">{{ formatCurrency(estimatedMargin) }}</span>
            </div>

            <div v-if="totalPublic > 0" class="flex justify-center pt-2">
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" :class="{
                'bg-green-50 text-green-600': marginClass === 'high',
                'bg-amber-50 text-amber-600': marginClass === 'medium',
                'bg-red-50 text-red-500': marginClass === 'low'
              }">
                <i class="fas fa-chart-line text-[10px]"></i>
                {{ marginPercent }}% de marge
              </span>
            </div>
          </div>
        </div>

        <!-- Chronologie -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-4">Chronologie</h3>

          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i class="fas fa-plus text-green-600 text-[10px]"></i>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-gray-900">Commande créée</p>
                <p class="text-[11px] text-gray-400">{{ formatDateTime(po.created_at) }}</p>
                <p v-if="po.user?.name" class="text-[11px] text-gray-300">{{ po.user.name }}</p>
              </div>
            </div>

            <div v-if="po.updated_at && po.updated_at !== po.created_at" class="flex items-start gap-3">
              <div class="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i class="fas fa-pen text-amber-500 text-[10px]"></i>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-gray-900">Dernière modification</p>
                <p class="text-[11px] text-gray-400">{{ formatDateTime(po.updated_at) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Annuler commande -->
    <transition name="modal">
      <div v-if="showCancelModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click="showCancelModal = false">
        <div class="bg-white rounded-xl max-w-md w-full shadow-xl" @click.stop>
          <div class="p-6 text-center">
            <div class="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-exclamation-triangle text-amber-500"></i>
            </div>
            <h3 class="text-base font-semibold text-gray-900 mb-1">Annuler cette commande ?</h3>
            <p class="text-xs text-gray-400">Cette action marquera la commande comme annulée. Êtes-vous sûr de vouloir continuer ?</p>
          </div>
          <div class="flex gap-3 px-6 pb-6">
            <button class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              @click="showCancelModal = false">
              Non, garder
            </button>
            <button
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="confirmCancel" :disabled="cancelPurchaseOrderLoading">
              <i v-if="cancelPurchaseOrderLoading" class="fas fa-spinner fa-spin text-[10px]"></i>
              Oui, annuler
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import purchaseOrderService from '@/services/purchaseOrderService'
import { useToastStore } from '@/store/modules/toastStore'
export default defineComponent({
  name: 'PurchaseOrderDetailsView',
  setup() {
    const route = useRoute()
    const toastStore = useToastStore()

    const po = ref(null)
    const loading = ref(true)
    const showCancelModal = ref(false)
    const showPrintOptions = ref(false)
    const cancelPurchaseOrderLoading = ref(false)

    const loadDetails = async () => {
      loading.value = true
      try {
        const id = route.params.id
        const res = await purchaseOrderService.getPurchaseOrder(id, {})
        po.value = res.data?.data || res.data || {}
        po.value.items = po.value.items || po.value.purchase_order_items || po.value.sale_items || []
      } catch (e) {
        console.error('Error loading order:', e)
        po.value = null
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement de la commande',
          duration: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const totalPurchase = computed(() => {
      const items = po.value?.items || []
      return items.reduce((s, it) => {
        return s + (Number(it.total_amount) || (Number(it.quantity) * Number(it.purchase_price) - (Number(it.discount) || 0)))
      }, 0)
    })

    const totalPublic = computed(() => {
      const items = po.value?.items || []
      return items.reduce((s, it) => {
        return s + ((Number(it.quantity) || 0) * (Number(it.sale_price) || 0))
      }, 0)
    })

    const estimatedMargin = computed(() => {
      return totalPublic.value - totalPurchase.value
    })

    const marginPercent = computed(() => {
      if (totalPublic.value === 0) return 0
      return ((estimatedMargin.value / totalPublic.value) * 100).toFixed(1)
    })

    const marginClass = computed(() => {
      const margin = parseFloat(marginPercent.value)
      if (margin >= 30) return 'high'
      if (margin >= 15) return 'medium'
      return 'low'
    })

    const getStatusLabel = (status) => {
      const map = { active: 'Active', inactive: 'Inactive', cancelled: 'Annulée' }
      return map[status] || status
    }

    const getStatusClass = (status) => {
      const map = { active: 'status-active', inactive: 'status-inactive', cancelled: 'status-cancelled' }
      return map[status] || 'status-inactive'
    }

    const getStatusVariant = (status) => {
      const map = { active: 'success', inactive: 'gray', cancelled: 'danger' }
      return map[status] || 'gray'
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0) + ' FCFA'
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      })
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    const confirmCancel = async () => {
      try {
        cancelPurchaseOrderLoading.value = true
        const response = await purchaseOrderService.cancelPurchaseOrder(po.value.id);
        console.log('response:', response)
        toastStore.addToast({
          type: 'success',
          message: 'Commande annulée avec succès',
          duration: 3000
        })
        cancelPurchaseOrderLoading.value = false
        showCancelModal.value = false
        loadDetails()
      } catch (e) {
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de l\'annulation',
          duration: 3000
        })
      }
    }

    const printPurchaseOrder = async () => {
      try {
        await purchaseOrderService.printPurchaseOrder(po.value.id);
      } catch (error) {
        console.error('Erreur impression commande fournisseur:', error);
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de l\'impression de la commande fournisseur',
          duration: 3000
        })
      }
    }

    onMounted(loadDetails)

    return {
      po,
      loading,
      totalPurchase,
      totalPublic,
      estimatedMargin,
      marginPercent,
      marginClass,
      showCancelModal,
      showPrintOptions,
      printPurchaseOrder,
      getStatusLabel,
      getStatusClass,
      getStatusVariant,
      formatCurrency,
      formatDate,
      formatDateTime,
      getInitials,
      confirmCancel,
      cancelPurchaseOrderLoading
    }
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
